// Libs
import { z } from "zod";
import { DocumentSnapshot, Timestamp } from "firebase/firestore";
import { NotFoundError } from "../error-classes";
import { dropUndefined, guardIsPlainObject } from "../utils";
import { EmailDeliveryStatus } from "./email-campaign";

// Universal email tracking schema - stored as individual documents
export const SentEmailSchema = z.object({
  // Reference fields - at least one should be populated
  campaignID: z.string().min(1).max(200).optional(), // Links to email campaign
  invoiceID: z.string().min(1).max(200).optional(), // Links to invoice
  estimateID: z.string().min(1).max(200).optional(), // Links to estimate
  taskID: z.string().min(1).max(200).optional(), // Links to task (reminders, etc.)

  // Email type for categorization
  emailType: z.enum([
    "campaign",
    "invoice",
    "estimate",
    "appointment_reminder",
    "feedback_request",
    "receipt",
    "work_authorization",
    "other",
  ]),

  // Recipient information
  customerID: z.string().min(1).max(200).optional(), // May be null for CSV uploads or non-customer emails
  customerName: z.string().min(1).max(200),
  emailAddress: z.string().email(),

  // Delivery tracking
  status: z
    .nativeEnum(EmailDeliveryStatus)
    .default(EmailDeliveryStatus.PENDING),
  postmarkMessageID: z.string().optional(), // Postmark message ID for tracking
  sentAt: z.instanceof(Timestamp).optional(),
  deliveredAt: z.instanceof(Timestamp).optional(),
  openedAt: z.instanceof(Timestamp).optional(),
  clickedAt: z.instanceof(Timestamp).optional(),
  bouncedAt: z.instanceof(Timestamp).optional(),
  spamComplaintAt: z.instanceof(Timestamp).optional(),
  errorMessage: z.string().optional(),

  // Metadata
  timestampCreated: z.instanceof(Timestamp),
  timestampLastModified: z.instanceof(Timestamp),

  // Template data for this specific recipient
  templateData: z
    .object({
      customer_name: z.string(),
      customer_address: z.string().optional(),
      // Add more template variables as needed
    })
    .optional(),
});

export type SentEmail = z.infer<typeof SentEmailSchema>;

export interface ExistingSentEmail extends SentEmail {
  id: string;
  refPath: string;
}

export const SentEmailManager = {
  /**
   * Convert the Document Snapshot into a validated ExistingSentEmail object.
   */
  createFromSnapshot: createSentEmailFromSnapshot,
  /** Drop `id` and `refPath` before saving to the database */
  convertForFirestore: convertSentEmailForFirestore,
  /** Use when validating something outgoing - writing to the DB or reading from the user */
  parse: validateSentEmail,
};

/**
 * Convert the Document Snapshot into a validated ExistingSentEmail object.
 */
function createSentEmailFromSnapshot(
  snapshot: DocumentSnapshot,
): ExistingSentEmail {
  if (!snapshot.exists()) {
    throw new NotFoundError(
      `SentEmail document does not exist: ${snapshot.ref.path}`,
    );
  }

  const data = snapshot.data();
  return {
    id: snapshot.id,
    refPath: snapshot.ref.path,
    ...validateSentEmail(data),
  };
}

function convertSentEmailForFirestore(sentEmail: SentEmail): any {
  const local = Object.assign({}, sentEmail);
  return dropUndefined(local);
}

function validateSentEmail(value: unknown): SentEmail {
  if (!guardIsPlainObject(value)) {
    throw new Error(`value not an object: ${value}`);
  }
  const result = SentEmailSchema.parse(value);
  return result;
}
